<script lang="ts" setup>
  import { cva } from 'class-variance-authority'
  import type { PromotionalBannerStoryblok } from '@/storyblok/component-types-sb'
  import { useIconStore } from '@/components/icon/store'

  const props = defineProps<{
    blok: PromotionalBannerStoryblok
  }>()

  const blok = computed(() => props.blok.content ?? props.blok)

  const iconStore = useIconStore()
  const icon = computed(() => iconStore.safeIcon(blok.value.labelIcon))
  const iconTooltipText = computed(() => blok.value.iconTooltipText?.trim() || null)
  const showIconTooltip = computed(() => icon.value === 'common/outline-info' && iconTooltipText.value)
  const isClosable = computed(() => Boolean(blok.value.closable))
  const dayjs = useDayjs()
  const { pageType } = usePageType()
  const isClosed = ref(false)

  const isVisible = computed(() => {
    if (isClosed.value) return false
    if (!blok.value.showInPages?.length) return false
    if (!blok.value.showInPages.includes(pageType.value)) return false

    const currentTime = dayjs.utc()
    const from = blok.value.startDate ? dayjs.utc(blok.value.startDate).startOf('day') : null
    const to = blok.value.endDate ? dayjs.utc(blok.value.endDate).endOf('day') : null

    const hasValidFrom = from?.isValid() ?? false
    const hasValidTo = to?.isValid() ?? false

    if (!hasValidFrom && !hasValidTo) return true

    if (hasValidFrom && hasValidTo) {
      return currentTime.isBetween(from, to, 'millisecond', '[]')
    }

    if (hasValidFrom) {
      return currentTime.isSameOrAfter(from)
    }

    if (hasValidTo) {
      return currentTime.isSameOrBefore(to)
    }

    return true
  })

  const onClose = () => {
    if (!isClosable.value) return

    isClosed.value = true
  }

  const wrapperClass = computed(() =>
    cva('', {
      variants: {
        isSelfServiceTypePage: {
          true: '',
          false: 'md:mx-10'
        }
      }
    })({
      isSelfServiceTypePage: pageType.value === 'css'
    })
  )

  const containerClass = computed(() =>
    cva('relative mx-auto mt-4', {
      variants: {
        isSelfServiceTypePage: {
          true: 'px-4 md:max-w-[1120px] md:px-0',
          false: 'w-full xl:px-0 max-w-460'
        }
      }
    })({
      isSelfServiceTypePage: pageType.value === 'css'
    })
  )

  const bannerClass = computed(() =>
    cva('relative flex gap-2 overflow-hidden rounded-md sm:justify-center', {
      variants: {
        background: {
          green: 'bg-bt-green'
        }
      }
    })({
      background: blok.value.background
    })
  )

  const labelClass = computed(() =>
    cva(
      'my-2 inline-flex w-max shrink-0 items-center gap-2 whitespace-nowrap text-subtitle-3 sm:my-0 sm:ml-auto lg:text-subtitle-1',
      {
        variants: {
          labelColor: {
            red: 'bg-bt-grabout-red text-white rounded-md rounded-tl pl-3 pr-2 py-1'
          }
        }
      }
    )({
      labelColor: blok.value.labelColor === 'red' ? 'red' : undefined
    })
  )
</script>

<template>
  <div
    v-if="isVisible"
    :class="wrapperClass"
  >
    <div :class="containerClass">
      <div :class="bannerClass">
        <NuxtImg
          v-if="blok.backgroundImage?.filename"
          :src="blok.backgroundImage.filename"
          :alt="blok.backgroundImage.alt || ''"
          provider="storyblok"
          class="pointer-events-none absolute inset-y-0 right-0 z-0 h-full w-auto max-w-none object-contain object-right sm:hidden"
        />

        <div class="relative z-10 flex-1 sm:flex sm:justify-center md:w-full">
          <button
            v-if="isClosable"
            :aria-label="$t('aria.label.close')"
            class="absolute right-4 top-4 z-20 hidden text-bt-blue-dark/30 md:block"
            @click="onClose"
          >
            <Icon name="common/close" />
          </button>

          <NuxtImg
            v-if="blok.backgroundImageDesktopLeft?.filename"
            :src="blok.backgroundImageDesktopLeft.filename"
            :alt="blok.backgroundImageDesktopLeft.alt || ''"
            provider="storyblok"
            class="pointer-events-none hidden h-full w-auto max-w-none object-contain object-[left_bottom] sm:absolute sm:bottom-0 sm:right-full sm:mr-4 sm:block md:left-[20%] md:right-auto md:mr-0 md:-translate-x-1/2"
          />

          <div
            class="relative flex flex-col items-start pb-5 pl-4 pr-24 pt-2 sm:inline-flex sm:w-auto sm:flex-none sm:items-end sm:justify-center sm:gap-2 sm:px-4 sm:pb-4 sm:pt-6 sm:text-right md:py-7"
          >
            <div class="w-7/12 sm:w-auto">
              <div
                v-if="blok.labelText"
                :class="labelClass"
              >
                {{ blok.labelText }}
                <UiTooltip
                  v-if="showIconTooltip"
                  :text="iconTooltipText"
                  tooltip-style="info"
                >
                  <LazyIcon
                    v-if="icon"
                    :name="icon"
                    class="!text-lg"
                  />
                </UiTooltip>
                <LazyIcon
                  v-else-if="icon"
                  :name="icon"
                  class="!text-lg"
                />
              </div>
              <div class="text-h5 xl:text-h4">
                {{ blok.mainText }}
              </div>
            </div>

            <div
              v-if="blok.priceMain || blok.priceSubtext"
              class="absolute right-2 top-2 z-10 flex items-start gap-4 sm:absolute sm:bottom-0 sm:left-full sm:right-auto sm:top-0 sm:ml-4 sm:flex sm:items-center md:ml-4"
            >
              <div
                v-if="blok.backgroundImage?.filename"
                class="hidden h-full shrink-0 pt-2 sm:flex sm:items-end"
              >
                <NuxtImg
                  :src="blok.backgroundImage.filename"
                  :alt="blok.backgroundImage.alt || ''"
                  provider="storyblok"
                  class="pointer-events-none block h-[calc(100%-0.5rem)] w-auto max-w-none object-contain object-bottom"
                />
              </div>

              <UiBubbleBadge>
                <span class="text-lg font-bold md:text-2xl">
                  {{ blok.priceMain }}
                </span>
                <sup v-if="blok.priceSub">
                  {{ blok.priceSub }}
                </sup>
                <span
                  v-if="blok.priceUnit"
                  class="text-lg font-bold md:text-2xl"
                >
                  {{ blok.priceUnit }}
                </span>
              </UiBubbleBadge>
              <div
                v-if="blok.priceSubtext"
                class="text-body-3"
              >
                {{ blok.priceSubtext }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
